import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const STORAGE_KEY = 'link_cookie_consent_v1';

function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch { return null; }
}
function setConsent(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...v, at: new Date().toISOString() })); } catch { /* ignore */ }
}

/**
 * Barra emergente de cookies (esquina inferior izquierda). Aparece hasta que
 * el usuario decida aceptar o rechazar. Una vez cerrado, deja una pestaña
 * flotante mínima para poder reabrir la política / cambiar preferencias.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [tabOpen, setTabOpen] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) setVisible(true);
    else setTabOpen(true);
  }, []);

  const accept = () => {
    setConsent({ decision: 'accepted', analytics: true });
    setVisible(false);
    setTabOpen(true);
  };
  const reject = () => {
    setConsent({ decision: 'rejected', analytics: false });
    setVisible(false);
    setTabOpen(true);
  };
  const reopen = () => {
    setTabOpen(false);
    setVisible(true);
  };

  return (
    <>
      {visible && (
        <div
          className="fixed z-[150] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md bg-white/95 backdrop-blur border border-black/10 rounded-2xl shadow-xl p-4 sm:p-5"
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-title"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#ED6B24]/10 text-[#ED6B24] flex items-center justify-center flex-shrink-0">
              <Cookie size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="cookie-title" className="text-sm font-bold text-[#58677A] mb-1" style={{ fontFamily: 'Gotham, sans-serif' }}>
                Usamos cookies para mejorar tu experiencia
              </h3>
              <p className="text-xs text-[#58677A]/85 leading-relaxed">
                Usamos cookies propias y de terceros para el funcionamiento del sitio, analítica y mejora de la experiencia. Puedes aceptarlas o rechazarlas. Consulta nuestro{' '}
                <Link
                  to="/aviso-privacidad"
                  className="text-[#ED6B24] hover:underline font-semibold"
                  data-testid="cookie-open-privacy"
                >
                  Aviso de Privacidad
                </Link>.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={accept}
                  className="bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-full px-4 py-2 transition-colors"
                  data-testid="cookie-accept"
                >
                  Aceptar
                </button>
                <button
                  onClick={reject}
                  className="bg-white border border-[#58677A]/30 text-[#58677A] hover:bg-[#58677A]/5 text-xs font-semibold rounded-full px-4 py-2 transition-colors"
                  data-testid="cookie-reject"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tabOpen && !visible && (
        <button
          onClick={reopen}
          className="fixed z-[140] bottom-4 left-4 bg-white/95 backdrop-blur border border-black/10 rounded-full shadow-lg pl-3 pr-4 py-2 flex items-center gap-2 text-xs font-semibold text-[#58677A] hover:text-[#ED6B24] transition-colors"
          aria-label="Ajustar cookies y privacidad"
          data-testid="cookie-tab"
        >
          <Cookie size={14} className="text-[#ED6B24]" />
          Cookies & Privacidad
        </button>
      )}
    </>
  );
}
